import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Brain,
  ArrowLeft,
  ArrowRight,
  Calendar,
  FileText,
  Loader2,
  CheckCircle,
  AlertCircle,
  Sparkles,
  AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PlanStudioWizard = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  // Check if user has @yomev.org email
  const isYomevUser = user?.email?.toLowerCase().endsWith('@yomev.org');
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  
  // Data
  const [versions, setVersions] = useState([]);
  const [weekMaps, setWeekMaps] = useState([]);
  
  // Form
  const [formData, setFormData] = useState({
    versionId: '',
    year: 1,
    week: 1,
    planType: 'CORE',
    additionalContext: ''
  });
  
  const [selectedWeekMap, setSelectedWeekMap] = useState(null);
  const [generatedPlan, setGeneratedPlan] = useState(null);

  useEffect(() => {
    if (!isYomevUser) {
      navigate('/plan-studio');
      return;
    }
    fetchVersions();
  }, [isYomevUser]);

  useEffect(() => {
    if (formData.versionId) {
      fetchWeekMaps();
    }
  }, [formData.versionId, formData.year]);

  useEffect(() => {
    if (formData.versionId && formData.year && formData.week) {
      const wm = weekMaps.find(w => w.year === formData.year && w.week === formData.week);
      setSelectedWeekMap(wm || null);
    }
  }, [formData.year, formData.week, weekMaps]);

  const fetchVersions = async () => {
    try {
      const res = await fetch(`${API}/api/plan-studio/versions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVersions(data);
        if (data.length > 0) {
          const activeVersion = data.find(v => v.isActive) || data[0];
          setFormData(prev => ({ ...prev, versionId: activeVersion.id }));
        }
      }
    } catch (error) {
      console.error('Error fetching versions:', error);
    }
  };

  const fetchWeekMaps = async () => {
    try {
      const res = await fetch(
        `${API}/api/plan-studio/week-maps?version_id=${formData.versionId}&year=${formData.year}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setWeekMaps(data);
      }
    } catch (error) {
      console.error('Error fetching week maps:', error);
    }
  };

  const [generationProgress, setGenerationProgress] = useState('');

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    setGenerationProgress('Plan kaydı oluşturuluyor...');
    
    try {
      const res = await fetch(`${API}/api/plan-studio/generate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        const plan = await res.json();
        setGenerationProgress('AI ile içerik oluşturuluyor... (Bu işlem 30-60 saniye sürebilir)');
        
        // Poll for completion
        const pollStatus = async () => {
          const maxAttempts = 60; // 2 minutes max
          let attempts = 0;
          
          while (attempts < maxAttempts) {
            await new Promise(r => setTimeout(r, 2000)); // Wait 2 seconds
            attempts++;
            
            try {
              const statusRes = await fetch(`${API}/api/plan-studio/plans/${plan.id}/status`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              
              if (statusRes.ok) {
                const status = await statusRes.json();
                
                if (status.generationStatus === 'completed') {
                  // Fetch full plan
                  const fullPlanRes = await fetch(`${API}/api/plan-studio/plans/${plan.id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  if (fullPlanRes.ok) {
                    const fullPlan = await fullPlanRes.json();
                    setGeneratedPlan(fullPlan);
                    setStep(4);
                    setGenerating(false);
                    return;
                  }
                } else if (status.generationStatus === 'failed') {
                  setError(status.errors?.join(', ') || 'Plan oluşturma başarısız oldu');
                  setGenerating(false);
                  return;
                } else if (status.generationStatus === 'generating') {
                  setGenerationProgress(`AI ile içerik oluşturuluyor... (${attempts * 2}s)`);
                }
              }
            } catch (pollError) {
              console.error('Polling error:', pollError);
            }
          }
          
          // Timeout
          setError('Plan oluşturma zaman aşımına uğradı. Lütfen tekrar deneyin.');
          setGenerating(false);
        };
        
        pollStatus();
      } else {
        const errorData = await res.json();
        setError(errorData.detail || 'Plan oluşturulurken bir hata oluştu');
        setGenerating(false);
      }
    } catch (error) {
      setError('Bağlantı hatası: ' + error.message);
      setGenerating(false);
    }
  };

  const planTypeOptions = [
    { value: 'CORE', label: 'Temel Gelişim Planı', description: 'Tüm gelişim alanlarını kapsayan genel plan', color: 'purple' },
    { value: 'TURKCE', label: 'Türkçe Dil Etkinlikleri', description: 'Dinleme, konuşma, okuryazarlık hazırlığı', color: 'blue' },
    { value: 'MATEMATIK', label: 'Matematik Etkinlikleri', description: 'Sayı, örüntü, ölçme, geometri', color: 'orange' }
  ];

  return (
    <div className="max-w-4xl mx-auto" data-testid="plan-studio-wizard">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/plan-studio')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft size={20} />
          Plan Studio'ya Dön
        </button>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Sparkles className="w-8 h-8 text-blue-600" />
          Yeni Plan Oluştur
        </h1>
        <p className="text-gray-600 mt-1">AI destekli haftalık plan oluşturma sihirbazı</p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {[
            { num: 1, label: 'Program Seçimi' },
            { num: 2, label: 'Hafta Seçimi' },
            { num: 3, label: 'Plan Türü' },
            { num: 4, label: 'Sonuç' }
          ].map((s, idx) => (
            <React.Fragment key={s.num}>
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                  step >= s.num 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {step > s.num ? <CheckCircle size={20} /> : s.num}
                </div>
                <span className={`text-xs mt-1 ${step >= s.num ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
                  {s.label}
                </span>
              </div>
              {idx < 3 && (
                <div className={`flex-1 h-1 mx-2 rounded transition-colors ${
                  step > s.num ? 'bg-blue-600' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {/* Step 1: Program Selection */}
        {step === 1 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Program Versiyonu Seçin</h2>
            
            {versions.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Henüz program versiyonu tanımlanmamış</p>
                <p className="text-sm text-gray-400 mt-1">Admin panelinden program versiyonu ekleyin</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {versions.map((version) => (
                  <button
                    key={version.id}
                    onClick={() => setFormData({ ...formData, versionId: version.id })}
                    className={`p-4 border-2 rounded-xl text-left transition-all ${
                      formData.versionId === version.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`version-${version.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900">{version.name}</h3>
                        <p className="text-sm text-gray-500">{version.description || 'Program açıklaması yok'}</p>
                      </div>
                      {version.isActive && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                          Aktif
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={() => setStep(2)}
                disabled={!formData.versionId}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Devam Et
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Week Selection */}
        {step === 2 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Yıl ve Hafta Seçin</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Program Yılı
                </label>
                <div className="flex gap-3">
                  {[1, 2, 3].map((year) => (
                    <button
                      key={year}
                      onClick={() => setFormData({ ...formData, year, week: 1 })}
                      className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                        formData.year === year
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      data-testid={`year-${year}`}
                    >
                      {year}. Yıl
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hafta Numarası (1-36)
                </label>
                <select
                  value={formData.week}
                  onChange={(e) => setFormData({ ...formData, week: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="week-select"
                >
                  {Array.from({ length: 36 }, (_, i) => i + 1).map((week) => {
                    const quarter = Math.ceil(week / 9);
                    return (
                      <option key={week} value={week}>
                        {week}. Hafta (Ç{quarter})
                      </option>
                    );
                  })}
                </select>
                <p className="text-xs text-gray-500 mt-1">Her çeyrek 9 hafta (4 çeyrek × 9 = 36 hafta)</p>
              </div>
            </div>

            {/* Week Map Preview */}
            {selectedWeekMap ? (
              <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-green-800">Hafta Haritası Bulundu</h4>
                    <p className="text-sm text-green-700 mt-1">
                      <strong>Tema:</strong> {selectedWeekMap.theme}
                    </p>
                    <p className="text-sm text-green-700">
                      <strong>Ana Kazanım:</strong> {selectedWeekMap.mainCompetency}
                    </p>
                    {selectedWeekMap.developmentAreas?.length > 0 && (
                      <p className="text-sm text-green-700">
                        <strong>Gelişim Alanları:</strong> {selectedWeekMap.developmentAreas.join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-yellow-800">Hafta Haritası Bulunamadı</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      Bu hafta için tanımlı bir hafta haritası yok. AI genel bir plan oluşturacak.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <button
                onClick={() => setStep(1)}
                className="flex items-center gap-2 px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                <ArrowLeft size={18} />
                Geri
              </button>
              <button
                onClick={() => setStep(3)}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Devam Et
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Plan Type */}
        {step === 3 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Plan Türünü Seçin</h2>
            
            <div className="grid gap-4">
              {planTypeOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setFormData({ ...formData, planType: option.value })}
                  className={`p-4 border-2 rounded-xl text-left transition-all ${
                    formData.planType === option.value
                      ? `border-${option.color}-500 bg-${option.color}-50`
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`type-${option.value}`}
                >
                  <h3 className="font-medium text-gray-900">{option.label}</h3>
                  <p className="text-sm text-gray-500 mt-1">{option.description}</p>
                </button>
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ek Notlar (Opsiyonel)
              </label>
              <textarea
                value={formData.additionalContext}
                onChange={(e) => setFormData({ ...formData, additionalContext: e.target.value })}
                placeholder="AI'ya vermek istediğiniz ek bilgiler veya özel istekler..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                rows={3}
                data-testid="additional-context"
              />
            </div>

            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertCircle size={20} />
                  <span>{error}</span>
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <button
                onClick={() => setStep(2)}
                className="flex items-center gap-2 px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                <ArrowLeft size={18} />
                Geri
              </button>
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50"
                data-testid="generate-btn"
              >
                {generating ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    {generationProgress || 'İşleniyor...'}
                  </>
                ) : (
                  <>
                    <Sparkles size={18} />
                    AI ile Plan Oluştur
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Result */}
        {step === 4 && generatedPlan && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Plan Başarıyla Oluşturuldu!</h2>
              <p className="text-gray-600 mt-1">
                {generatedPlan.year}. Yıl - {generatedPlan.week}. Hafta {generatedPlan.planType} Planı
              </p>
            </div>

            {generatedPlan.contentJson && (
              <div className="p-4 bg-gray-50 rounded-xl">
                <h3 className="font-medium text-gray-900 mb-2">
                  Tema: {generatedPlan.contentJson.theme}
                </h3>
                <p className="text-sm text-gray-600">
                  <strong>Ana Kazanım:</strong> {generatedPlan.contentJson.mainCompetency}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Haftalık Hedef:</strong> {generatedPlan.contentJson.weeklyObjective}
                </p>
              </div>
            )}

            {generatedPlan.validationWarnings?.length > 0 && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                <h4 className="font-medium text-yellow-800 mb-2">Uyarılar</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  {generatedPlan.validationWarnings.map((warning, idx) => (
                    <li key={idx}>• {warning}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-center gap-4">
              <button
                onClick={() => navigate(`/plan-studio/review/${generatedPlan.id}`)}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                data-testid="view-plan-btn"
              >
                <FileText size={18} />
                Planı İncele ve Düzenle
              </button>
              <button
                onClick={() => {
                  setStep(1);
                  setGeneratedPlan(null);
                  setFormData({
                    versionId: formData.versionId,
                    year: 1,
                    week: 1,
                    planType: 'CORE',
                    additionalContext: ''
                  });
                }}
                className="flex items-center gap-2 px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                <Brain size={18} />
                Yeni Plan Oluştur
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanStudioWizard;
